import React, { useContext, useEffect, useRef } from "react";
import { Chart, registerables } from "chart.js";
import { FutureDataContent } from ".";
import { useTradingColors } from "../Spot/tradingColorConfig";

Chart.register(...registerables);

const FututeDepthChart = ({ isFullscreen = false }) => {
  const { finalBiddata, finalAskdata, currencycode, basecurrency } =
    useContext(FutureDataContent);
  const { upColor, downColor, upBg, downBg } = useTradingColors();
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  const formatDataForDepthChart = (data) =>
    data?.map(({ price, amount }) => [Number(price), Number(amount)]);

  useEffect(() => {
    const askDataForDepthChart = formatDataForDepthChart(finalAskdata);
    const bidDataForDepthChart = formatDataForDepthChart(finalBiddata);

    if (!askDataForDepthChart?.length || !bidDataForDepthChart?.length) {
      return;
    }

    const cumulativeBids = bidDataForDepthChart
      .map((bid, i) => ({
        price: bid[0],
        volume: bidDataForDepthChart
          .slice(0, i + 1)
          .reduce((acc, b) => acc + b[1], 0),
      }))
      .reverse();

    const cumulativeAsks = askDataForDepthChart.map((ask, i) => ({
      price: ask[0],
      volume: askDataForDepthChart
        .slice(0, i + 1)
        .reduce((acc, a) => acc + a[1], 0),
    }));

    // Find midpoint to bridge gap
    const highestBid = cumulativeBids[cumulativeBids.length - 1].price;
    const lowestAsk = cumulativeAsks[0].price;
    const midpointPrice = (highestBid + lowestAsk) / 2;

    cumulativeBids.push({
      price: midpointPrice,
      volume: cumulativeBids[cumulativeBids.length - 1].volume,
    });
    cumulativeAsks.unshift({
      price: midpointPrice,
      volume: cumulativeAsks[0].volume,
    });

    const allPrices = [
      ...cumulativeBids.map((bid) => bid.price),
      ...cumulativeAsks.map((ask) => ask.price),
    ];

    const bidVolumes = cumulativeBids.map((bid) => bid.volume);
    const askVolumes = cumulativeAsks.map((ask) => ask.volume);

    if (chartInstance.current) {
      chartInstance.current.data.labels = allPrices;
      chartInstance.current.data.datasets[0].data = [
        ...bidVolumes,
        ...new Array(cumulativeAsks.length).fill(null),
      ];
      chartInstance.current.data.datasets[0].borderColor = upColor;
      chartInstance.current.data.datasets[0].backgroundColor =
        upBg || "rgba(33, 203, 128, 0.5)";
      chartInstance.current.data.datasets[1].data = [
        ...new Array(cumulativeBids.length).fill(null),
        ...askVolumes,
      ];
      chartInstance.current.data.datasets[1].borderColor = downColor;
      chartInstance.current.data.datasets[1].backgroundColor =
        downBg || "rgba(245, 71, 93, 0.5)";
      chartInstance.current.update();
    } else {
      const ctx = chartRef.current.getContext("2d");
      chartInstance.current = new Chart(ctx, {
        type: "line",
        data: {
          labels: allPrices,
          datasets: [
            {
              data: [
                ...bidVolumes,
                ...new Array(cumulativeAsks.length).fill(null),
              ],
              borderColor: upColor,
              backgroundColor: upBg || "rgba(33, 203, 128, 0.5)",
              stepped: true,
              fill: true,
            },
            {
              data: [
                ...new Array(cumulativeBids.length).fill(null),
                ...askVolumes,
              ],
              borderColor: downColor,
              backgroundColor: downBg || "rgba(245, 71, 93, 0.5)",
              stepped: true,
              fill: true,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: {
            mode: "index",
            intersect: false,
          },
          plugins: {
            legend: {
              display: false,
            },
            tooltip: {
              enabled: true,
              callbacks: {
                label: (tooltipItem) => {
                  return `Price: ${tooltipItem.label}, Volume: ${tooltipItem.raw}`;
                },
              },
            },
          },
          scales: {
            x: {
              title: {
                display: true,
                text: "Price",
              },
            },
            y: {
              title: {
                display: true,
                text: "Cumulative Volume",
              },
            },
          },
        },
      });
    }
  }, [finalBiddata, finalAskdata, currencycode, basecurrency, upColor, downColor, upBg, downBg]);

  return (
    <div style={{ width: "100%", height: "100%", minHeight: isFullscreen ? 0 : "470px" }}>
      <canvas ref={chartRef} style={{ height: "100%", width: "100%" }} />
    </div>
  );
};

export default FututeDepthChart;
